/**
 * AuthServiceModule — wraps vibe-designer's GET /api/me.
 *
 * Returns the per-session tenant + user identity. Cached after first
 * successful fetch — re-fetching is cheap but the response is
 * effectively immutable for the JWT's lifetime, and the 401-handler
 * in vibe-designer's api.ts redirects to /login on stale token (we
 * inherit that behavior because we route through the same fetch).
 *
 * isAuthenticated returns true iff GET /api/me has succeeded at least
 * once + the response carried a tenant_id. Standalone variant always
 * returns true (single-user, no auth).
 */
import type { AuthService } from '../types';
import { getAuthHeaders } from './authHeaders';

const ME_PATH = '/api/me';

interface MeResponse {
  tenant_id?: string;
  user_id?: string;
  email?: string;
}

export class AuthServiceModule implements AuthService {
  private cached: MeResponse | null = null;
  private fetchPromise: Promise<MeResponse | null> | null = null;

  constructor(
    private readonly mePath: string = ME_PATH,
    // See PersistenceServiceModule for the "Illegal invocation" rationale.
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  isAuthenticated(): boolean {
    return this.cached !== null && typeof this.cached.tenant_id === 'string';
  }

  getTenantId(): string | null {
    return this.cached?.tenant_id ?? null;
  }

  getUserId(): string | null {
    return this.cached?.user_id ?? null;
  }

  async refresh(): Promise<MeResponse | null> {
    if (this.fetchPromise !== null) {
      return this.fetchPromise;
    }
    this.fetchPromise = (async () => {
      try {
        const resp = await this.fetchFn(this.mePath, {
          headers: getAuthHeaders(),
        });
        if (!resp.ok) {
          return null;
        }
        const body = (await resp.json()) as MeResponse;
        this.cached = body;
        return body;
      } catch {
        return null;
      } finally {
        this.fetchPromise = null;
      }
    })();
    return this.fetchPromise;
  }
}
